import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

import { db } from "../firebaseConfig";
import { ref, onValue, off } from "firebase/database";
import { useFavorites } from "./Context/FavoritesContext";

import "../styles/propertyListing.css";
import "../styles/filterModal.css";

const CATEGORIAS = [
  "Casa",
  "Apartamento",
  "Lote",
  "Pousada",
  "Sitio",
  "Fazenda",
  "Terreno",
  "Galpão",
  "Sala Comercial",
  "Imovel Comercial",
  "Imovel na Planta",
  "Chale",
  "Kitnet",
  "Em Construcao",
];

const CONTADORES = ["Qualquer", 1, 2, 3, 4, 5, 6, 7, 8];

function FilterModal({ isOpen, onClose }) {
  const { filters, setFilters, setSearchApplied, searchApplied, clearSearch } =
    useFavorites();

  const [cidades, setCidades] = useState([]);
  const [bairros, setBairros] = useState([]);
  const [cidadeSelecionada, setCidadeSelecionada] = useState([]);
  const [bairroSelecionado, setBairroSelecionado] = useState([]);

  const [selectedCategorias, setSelectedCategorias] = useState([]);

  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();

  const [totalImoveis, setTotalImoveis] = useState([]);
  const [filteredImoveis, setFilteredImoveis] = useState([]);

  const [selectedRooms, setSelectedRooms] = useState("Qualquer");
  const [selectedBathrooms, setSelectedBathrooms] = useState("Qualquer");
  const [selectedGarages, setSelectedGarages] = useState("Qualquer");

  const [selectedComodidades, setSelectedComodidades] = useState([]);

  const [isBuying, setIsBuying] = useState(true);
  const [value, setValue] = useState([10000, 5000000]);

  const [codigoImovel, setCodigoImovel] = useState("");

  const comodidades = useMemo(
    () => [
      "Armário planejado",
      "Condomínio Fechado",
      "Escritório",
      "Elevador",
      "Espaço Gourmet",
      "Piscina",
    ],
    []
  );

  // Navega para a página de resultados. Esse clique é o único momento em que
  // consideramos que o usuário "aplicou" uma pesquisa de verdade — os
  // efeitos abaixo recalculam filteredImoveis/filters.results o tempo todo
  // (inclusive no primeiro carregamento, antes de qualquer ação do usuário),
  // então searchApplied + criteria só são gravados aqui.
  const redirect = () => {
    if (filteredImoveis.length === 0) return;

    setSearchApplied(true);
    setFilters({
      ...filters,
      comodidades: selectedComodidades,
      results: filteredImoveis,
      criteria: {
        modality: isBuying ? "comprar" : "alugar",
        categorias: selectedCategorias,
        cidades: cidadeSelecionada
          .map((id) => cidades.find((c) => c.id === id)?.nome)
          .filter(Boolean),
        bairros: bairroSelecionado,
        quartos: selectedRooms,
        banheiros: selectedBathrooms,
        vagas: selectedGarages,
        comodidades: selectedComodidades,
        codigo: codigoImovel,
        precoMin: value[0],
        precoMax: value[1],
      },
    });
    onClose();
    navigate("/pesquisa");
  };

  // Carrega imóveis (status === true)
  useEffect(() => {
    const getImoveisRef = ref(db, "imoveisPublicos");
    const unsub = onValue(getImoveisRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const allImoveis = Object.values(data).filter(
          (imovel) => imovel?.imovelInfo?.status === true
        );
        setTotalImoveis(allImoveis);
        // mantém comportamento anterior: setFilters no carregamento
        setFilters(allImoveis);
      } else {
        setTotalImoveis([]);
        setFilters([]);
      }
    });
    return () => {
      try {
        off(getImoveisRef);
      } catch { }
      if (typeof unsub === "function") unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carrega cidades e bairros
  useEffect(() => {
    const getLocRef = ref(db, "loc/cidades");
    const unsub = onValue(getLocRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const cidadesArray = Object.keys(data)
          .map((key) => ({
            id: key,
            nome: data[key].nome,
            bairros: data[key].bairros
              ? Object.values(data[key].bairros).sort()
              : [],
          }))
          .sort((a, b) => a.nome.localeCompare(b.nome));
        setCidades(cidadesArray);
      } else {
        setCidades([]);
      }
    });
    return () => {
      try {
        off(getLocRef);
      } catch { }
      if (typeof unsub === "function") unsub();
    };
  }, []);

  // Formata moeda vinda como string "R$ 1.234,56" para número 1234.56
  const formatCurrencyToNumber = (valor) => {
    if (typeof valor !== "string") return 0;
    // remove tudo que não é dígito e divide por 100 (assumindo 2 casas)
    return parseFloat(valor.replace(/[\D]/g, "")) / 100;
  };

  // Aplica filtros sempre que algum estado relevante muda
  useEffect(() => {
    if (!totalImoveis || totalImoveis.length === 0) {
      setFilteredImoveis([]);
      setFilters({ ...filters, comodidades: selectedComodidades, results: [] });
      return;
    }

    let filtered = [...totalImoveis];

    // Modalidade
    filtered = filtered.filter(
      (imovel) =>
        (imovel?.imovelInfo?.modalidade || "")
          .trim()
          .toLowerCase() === (isBuying ? "venda" : "aluguel")
    );

    // Faixa de valor
    filtered = filtered.filter((imovel) => {
      const valor = formatCurrencyToNumber(imovel?.informacoesComerciais?.valor);

      // Se o usuário arrastar até o valor máximo, mostra tudo acima também
      if (isBuying) {
        const limiteMaximo = 10000000;
        return (
          valor >= value[0] &&
          (value[1] >= limiteMaximo ? true : valor <= value[1])
        );
      } else {
        const limiteMaximoAluguel = 10000;
        return (
          valor >= value[0] &&
          (value[1] >= limiteMaximoAluguel ? true : valor <= value[1])
        );
      }
    });

    // Quartos
    if (selectedRooms !== "Qualquer") {
      filtered = filtered.filter(
        (imovel) => (imovel?.caracteristicas?.quartos || 0) >= Number(selectedRooms)
      );
    }

    // Banheiros
    if (selectedBathrooms !== "Qualquer") {
      filtered = filtered.filter(
        (imovel) => (imovel?.caracteristicas?.banheiros || 0) >= Number(selectedBathrooms)
      );
    }

    // Garagens
    if (selectedGarages !== "Qualquer") {
      filtered = filtered.filter(
        (imovel) => (imovel?.caracteristicas?.vagas || 0) >= Number(selectedGarages)
      );
    }

    // Comodidades (qualquer uma das selecionadas)
    if (selectedComodidades.length > 0) {
      filtered = filtered.filter((imovel) => {
        if (!imovel?.selectedItems) return false;
        const comodidadesDoImovel = Object.values(imovel.selectedItems).map((i) =>
          String(i).trim().toLowerCase()
        );
        return selectedComodidades.some((filtro) =>
          comodidadesDoImovel.includes(String(filtro).trim().toLowerCase())
        );
        // se quiser comportamento "todas as comodidades", troca some() por every()
      });
    }

    if (selectedCategorias.length > 0) {
      filtered = filtered.filter((imovel) =>
        selectedCategorias.some((cat) =>
          (imovel?.imovelInfo?.tipo || "")
            .trim()
            .toLowerCase()
            .includes(cat.trim().toLowerCase())
        )
      );
    }

    // Cidade
    if (cidadeSelecionada.length > 0) {
      filtered = filtered.filter((imovel) =>
        cidadeSelecionada.some((id) => {
          const nomeCidade =
            cidades.find((c) => c.id === id)?.nome?.trim().toLowerCase() || "";
          return (
            (imovel?.imovelInfo?.cidade || "").trim().toLowerCase() === nomeCidade
          );
        })
      );
    }

    // Bairros (qualquer um dos selecionados)
    if (bairroSelecionado.length > 0) {
      filtered = filtered.filter((imovel) =>
        bairroSelecionado.some(
          (bairro) =>
            (imovel?.imovelInfo?.bairro || "").trim().toLowerCase() ===
            String(bairro).trim().toLowerCase()
        )
      );
    }

    // Código do imóvel (busca exata, case-insensitive)
    if (codigoImovel.trim() !== "") {
      const search = codigoImovel.toLowerCase().trim();

      filtered = filtered.filter((imovel) => {
        const codigo = String(imovel?.imovelInfo?.codigo || "").toLowerCase();
        return codigo === search;
      });
    }

    setFilteredImoveis(filtered);
    // mantém o mesmo contrato do seu contexto
    setFilters({
      ...filters,
      comodidades: selectedComodidades,
      results: filtered,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalImoveis,
    selectedRooms,
    selectedBathrooms,
    selectedGarages,
    isBuying,
    value,
    selectedComodidades,
    selectedCategorias,
    cidadeSelecionada,
    bairroSelecionado,
    codigoImovel,
  ]);

  // Ações de UI
  const handleModeChange = (buying) => {
    setIsBuying(buying);
    setValue(buying ? [10000, 10000000] : [100, 10000]);
  };

  const toggleCategoria = (tipo) => {
    setSelectedCategorias((prev) =>
      prev.includes(tipo) ? prev.filter((t) => t !== tipo) : [...prev, tipo]
    );
  };

  const handleComodidadeChange = (comodidade) => {
    setSelectedComodidades((prevSelected) =>
      prevSelected.includes(comodidade)
        ? prevSelected.filter((item) => item !== comodidade)
        : [...prevSelected, comodidade]
    );
  };

  const handleBairroChange = (bairro) => {
    setBairroSelecionado((prev) =>
      prev.includes(bairro)
        ? prev.filter((item) => item !== bairro)
        : [...prev, bairro]
    );
  };

  const handleCidadeChange = (novas) => {
    setCidadeSelecionada(novas);

    // Se apenas uma cidade, carrega bairros
    if (novas.length === 1) {
      const cidade = cidades.find((c) => c.id === novas[0]);
      setBairros(cidade ? cidade.bairros : []);
      setBairroSelecionado([]);
    } else {
      // Se mais de uma, limpa bairros
      setBairros([]);
      setBairroSelecionado([]);
    }

    setMenuOpen(false);
  };

  // Reseta só os campos locais do formulário (sem mexer em searchApplied/
  // filters.criteria) — usado tanto por "Limpar filtros" quanto pelo efeito
  // abaixo, que reage quando a busca é limpa de fora do modal.
  const resetLocalFields = () => {
    setSelectedRooms("Qualquer");
    setSelectedBathrooms("Qualquer");
    setSelectedGarages("Qualquer");
    setCidadeSelecionada([]);
    setBairros([]);
    setBairroSelecionado([]);
    setValue([10000, 5000000]);
    setIsBuying(true);
    setSelectedComodidades([]);
    setSelectedCategorias([]);
    setCodigoImovel("");
  };

  const removeFilters = () => {
    resetLocalFields();
    setFilteredImoveis(totalImoveis);
    clearSearch();
  };

  // Se a busca for limpa por fora do modal (ex.: botão "Limpar busca" em
  // /pesquisa, que só mexe no contexto), o FilterModal continua montado no
  // Header e precisa ficar sabendo para não reabrir com os campos antigos
  // preenchidos. Só reage à transição true -> false: não roda no mount
  // (searchApplied já nasce false) nem quando uma busca é aplicada.
  const wasSearchApplied = useRef(searchApplied);
  useEffect(() => {
    if (wasSearchApplied.current && !searchApplied) {
      resetLocalFields();
    }
    wasSearchApplied.current = searchApplied;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchApplied]);

  // UI

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{ className: "filter-modal__paper" }}
    >
      <div className="filter-modal">
        <div className="filter-modal__header">
          <div>
            <h2>Encontre o imóvel ideal</h2>
            <p>Refine sua busca em poucos passos.</p>
          </div>
          <button
            type="button"
            className="filter-modal__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            <IoClose size={20} />
          </button>
        </div>

        <div className="filter-modal__body">
          {/* COMPRAR / ALUGAR */}
          <div className="filter-modal__section">
            <span className="filter-modal__section-label">
              Comprar ou alugar
            </span>
            <div
              className="listing-filters__segmented"
              role="group"
              aria-label="Comprar ou alugar"
            >
              <button
                type="button"
                className={isBuying ? "is-active" : undefined}
                onClick={() => handleModeChange(true)}
              >
                Comprar
              </button>
              <button
                type="button"
                className={!isBuying ? "is-active" : undefined}
                onClick={() => handleModeChange(false)}
              >
                Alugar
              </button>
            </div>
          </div>

          {/* TIPO DE IMÓVEL */}
          <div className="filter-modal__section">
            <span className="filter-modal__section-label">
              Tipo de imóvel
            </span>
            <div className="filter-modal__chips">
              {CATEGORIAS.map((tipo) => (
                <button
                  key={tipo}
                  type="button"
                  className={
                    selectedCategorias.includes(tipo) ? "is-active" : undefined
                  }
                  aria-pressed={selectedCategorias.includes(tipo)}
                  onClick={() => toggleCategoria(tipo)}
                >
                  {tipo}
                </button>
              ))}
            </div>
          </div>

          {/* LOCALIZAÇÃO */}
          <div className="filter-modal__section">
            <span className="filter-modal__section-label">Localização</span>
            <div className="filter-modal__grid-2">
              <TextField
                select
                fullWidth
                label={`Cidades (${cidadeSelecionada.length})`}
                size="small"
                SelectProps={{
                  multiple: true,
                  open: menuOpen,
                  onOpen: () => setMenuOpen(true),
                  onClose: () => setMenuOpen(false),
                  renderValue: (selected) =>
                    selected
                      .map(
                        (id) =>
                          cidades.find((c) => c.id === id)?.nome || "Desconhecida"
                      )
                      .join(", "),
                  MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                }}
                value={cidadeSelecionada}
                onChange={(e) => handleCidadeChange(e.target.value)}
                sx={{
                  "& label.Mui-focused": { color: "var(--verde)" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--radius-sm)",
                    "& fieldset": { borderColor: "var(--border-subtle)" },
                    "&:hover fieldset": { borderColor: "var(--verde)" },
                    "&.Mui-focused fieldset": { borderColor: "var(--verde)" },
                  },
                }}
              >
                {cidades.map((cidade) => (
                  <MenuItem key={cidade.id} value={cidade.id}>
                    <Checkbox
                      checked={cidadeSelecionada.includes(cidade.id)}
                      sx={{
                        color: "var(--verde)",
                        "&.Mui-checked": { color: "var(--verde)" },
                      }}
                    />
                    {cidade.nome}
                  </MenuItem>
                ))}
              </TextField>

              {cidadeSelecionada.length === 1 && bairros.length > 0 && (
                <div className="filter-modal__bairro-wrap">
                  <span className="filter-modal__pill-group-label">
                    Bairros em {cidades.find((c) => c.id === cidadeSelecionada[0])?.nome}
                  </span>
                  <div className="filter-modal__bairro-grid">
                    {[...bairros]
                      .sort((a, b) => a.localeCompare(b, "pt-BR"))
                      .map((bairro) => (
                        <FormControlLabel
                          key={bairro}
                          control={
                            <Checkbox
                              size="small"
                              checked={bairroSelecionado.includes(bairro)}
                              onChange={() => handleBairroChange(bairro)}
                              sx={{
                                color: "var(--verde)",
                                "&.Mui-checked": { color: "var(--verde)" },
                              }}
                            />
                          }
                          label={bairro}
                          sx={{ color: "var(--text-primary)", m: 0 }}
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CARACTERÍSTICAS */}
          <div className="filter-modal__section">
            <span className="filter-modal__section-label">Características</span>
            {[
              { label: "Quartos", selected: selectedRooms, setter: setSelectedRooms },
              {
                label: "Banheiros",
                selected: selectedBathrooms,
                setter: setSelectedBathrooms,
              },
              { label: "Vagas", selected: selectedGarages, setter: setSelectedGarages },
            ].map((group) => (
              <div key={group.label} className="filter-modal__pill-group">
                <span className="filter-modal__pill-group-label">
                  {group.label}
                </span>
                <div className="filter-modal__chips">
                  {CONTADORES.map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={group.selected === v ? "is-active" : undefined}
                      aria-pressed={group.selected === v}
                      onClick={() => group.setter(v)}
                    >
                      {v === "Qualquer" ? v : `${v}+`}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* FAIXA DE PREÇO */}
          <div className="filter-modal__section">
            <span className="filter-modal__section-label">Faixa de preço</span>
            <div className="filter-modal__grid-2">
              <TextField
                fullWidth
                size="small"
                label="Preço mínimo"
                value={
                  value[0]
                    ? new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                      maximumFractionDigits: 0,
                    }).format(value[0])
                    : ""
                }
                onChange={(e) => {
                  const numero = e.target.value.replace(/\D/g, "");
                  const novoMin = Number(numero);
                  setValue([novoMin, Math.max(novoMin, value[1])]);
                }}
                placeholder="R$ 0"
                InputProps={{ inputMode: "numeric" }}
                sx={{
                  "& label.Mui-focused": { color: "var(--verde)" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--radius-sm)",
                    "& fieldset": { borderColor: "var(--border-subtle)" },
                    "&:hover fieldset": { borderColor: "var(--verde)" },
                    "&.Mui-focused fieldset": { borderColor: "var(--verde)" },
                  },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Preço máximo"
                value={
                  value[1]
                    ? new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                      maximumFractionDigits: 0,
                    }).format(value[1])
                    : ""
                }
                onChange={(e) => {
                  const numero = e.target.value.replace(/\D/g, "");
                  const novoMax = Number(numero);
                  setValue([Math.min(value[0], novoMax), novoMax]);
                }}
                placeholder="R$ 0"
                InputProps={{ inputMode: "numeric" }}
                sx={{
                  "& label.Mui-focused": { color: "var(--verde)" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--radius-sm)",
                    "& fieldset": { borderColor: "var(--border-subtle)" },
                    "&:hover fieldset": { borderColor: "var(--verde)" },
                    "&.Mui-focused fieldset": { borderColor: "var(--verde)" },
                  },
                }}
              />
            </div>
          </div>

          {/* COMODIDADES */}
          <div className="filter-modal__section">
            <span className="filter-modal__section-label">
              Mais características
            </span>
            <div className="filter-modal__comodidades-grid">
              {comodidades.map((item) => (
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedComodidades.includes(item)}
                      onChange={() => handleComodidadeChange(item)}
                      sx={{
                        color: "var(--verde)",
                        "&.Mui-checked": { color: "var(--verde)" },
                      }}
                    />
                  }
                  label={item}
                  sx={{ color: "var(--text-primary)", m: 0 }}
                />
              ))}
            </div>
          </div>

          {/* CÓDIGO DO IMÓVEL */}
          <div className="filter-modal__section filter-modal__section--secondary">
            <span className="filter-modal__section-label">
              Código do imóvel
            </span>
            <TextField
              size="small"
              label="Ex.: 308"
              variant="outlined"
              value={codigoImovel}
              onChange={(e) => setCodigoImovel(e.target.value)}
              sx={{
                maxWidth: 260,
                "& label.Mui-focused": { color: "var(--verde)" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "var(--radius-sm)",
                  "& fieldset": { borderColor: "var(--border-subtle)" },
                  "&:hover fieldset": { borderColor: "var(--verde)" },
                  "&.Mui-focused fieldset": { borderColor: "var(--verde)" },
                },
              }}
            />
          </div>
        </div>

        <div className="filter-modal__footer">
          <button
            type="button"
            className="filter-modal__clear"
            onClick={removeFilters}
          >
            Limpar filtros
          </button>
          <button
            type="button"
            className="filter-modal__submit"
            onClick={redirect}
            disabled={filteredImoveis.length === 0}
          >
            Mostrar {filteredImoveis.length} imóveis
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export default FilterModal;
